/* eslint-disable max-lines */
import type { ProcessingActivitiesColumnName } from '@main/access-control-types';
import {
  ComplianceReportMetaField,
  TeamPreview,
  UserPreview,
} from '@main/access-control-types';
import type { ID, SchemaToType } from '@main/schema-utils';
import { mkInput, mkType } from '@main/schema-utils';

import { ProcessingActivitiesFiltersInput } from './processingActivity';

/**
 * Input for filtering compliance reports
 */
export const ComplianceReportsFilterInput = mkInput({
  name: 'ComplianceReportsFilterInput',
  comment: 'Input for filtering compliance reports',
  fields: {
    createdAtAfter: {
      comment: 'Filter for compliance reports created after this date',
      type: 'Date',
      optional: true,
    },
    createdAtBefore: {
      comment: 'Filter for compliance reports created before this date',
      type: 'Date',
      optional: true,
    },
    dataProtectionOfficerIds: {
      comment: 'The user IDs of the data protection officer',
      type: 'id',
      modelName: 'user',
      optional: true,
      list: true,
    },
    ids: {
      comment: 'The ids of the targeted compliance reports',
      type: 'id',
      modelName: 'complianceReport',
      list: true,
      optional: true,
    },
    ownerIds: {
      comment: 'The user IDs of the owners',
      type: 'id',
      modelName: 'user',
      optional: true,
      list: true,
    },
    teamIds: {
      comment: 'The team IDs of the teams',
      type: 'id',
      modelName: 'team',
      optional: true,
      list: true,
    },
    text: {
      comment:
        'Find any compliance reports with this text in the title or description',
      optional: true,
      type: 'string',
    },
    updatedAtAfter: {
      comment:
        'Filter for compliance reports most recently updated after this date',
      type: 'Date',
      optional: true,
    },
    updatedAtBefore: {
      comment:
        'Filter for compliance reports most recently updated before this date',
      type: 'Date',
      optional: true,
    },
  },
});

/** Override type */
export type ComplianceReportsFilterInput = SchemaToType<
  typeof ComplianceReportsFilterInput
>;

export const ComplianceReportSnapshotInput = mkInput({
  name: 'ComplianceReportSnapshotInput',
  comment: 'Input for fetching compliance report snapshots',
  fields: {
    complianceReportId: {
      type: 'id',
      modelName: 'complianceReport',
      comment: 'The ID of the compliance report to fetch snapshots for',
    },
  },
});

/** Override type */
export type ComplianceReportSnapshotInput = SchemaToType<
  typeof ComplianceReportSnapshotInput
>;

export const ComplianceReportSnapshotsFilterInput = mkInput({
  name: 'ComplianceReportSnapshotsFilterInput',
  comment: 'Input for filtering compliance report snapshots',
  fields: {
    createdAtAfter: {
      comment: 'Filter for compliance report snapshots created after this date',
      type: 'Date',
      optional: true,
    },
    createdAtBefore: {
      comment:
        'Filter for compliance report snapshots created before this date',
      type: 'Date',
      optional: true,
    },
    ids: {
      comment: 'The ids of the targeted compliance report snapshots',
      type: 'id',
      modelName: 'complianceReportSnapshot',
      list: true,
      optional: true,
    },
    text: {
      comment:
        'Find any compliance report versions with this text in the title',
      optional: true,
      type: 'string',
    },
  },
});

/** Override type */
export type ComplianceReportSnapshotsFilterInput = SchemaToType<
  typeof ComplianceReportSnapshotsFilterInput
>;

/**
 * Identifier of the processing activities UI column that the data for a compliance report column comes from
 */
export type ComplianceReportColumnSource =
  | ProcessingActivitiesColumnName
  | ID<'attributeKey'>;

export const ComplianceReportDataCell = mkType({
  name: 'ComplianceReportDataCell',
  comment:
    'Cell value within a compliance report row (isManualUpdate prevents overwrites on sync)',
  fields: {
    isManualUpdate: {
      comment:
        'Flag to indicate if the cell was manually updated (prevents overwrite on sync)',
      type: 'boolean',
    },
    value: {
      comment: 'Display value of the cell',
      type: 'string',
    },
  },
});

/** Override type */
export type ComplianceReportDataCell = SchemaToType<
  typeof ComplianceReportDataCell
>;

export const ComplianceReportDataRow = mkType({
  name: 'ComplianceReportDataRow',
  comment:
    'Row of processing activity data (processingActivityId null/omitted if manually added)',
  fields: {
    processingActivityId: {
      comment:
        'ID of the processing activity the row comes from; absent if manually added',
      type: 'id',
      modelName: 'processingActivity',
      optional: true,
    },
    cells: {
      comment: 'Ordered cells; index aligns with columns',
      list: true,
      type: ComplianceReportDataCell,
    },
  },
});

/** Override type */
export type ComplianceReportDataRow = SchemaToType<
  typeof ComplianceReportDataRow
>;

export const ComplianceReportDataColumn = mkType({
  name: 'ComplianceReportDataColumn',
  comment: 'Column definition for a compliance report',
  fields: {
    source: {
      comment:
        'Identifier for how column data is sourced, null if manually added',
      type: 'string',
      optional: true,
    },
    name: {
      comment: 'Display name of the column',
      type: 'string',
    },
  },
});

/** Override type */
export type ComplianceReportDataColumn = SchemaToType<
  typeof ComplianceReportDataColumn
>;

export const ComplianceReportData = mkType({
  name: 'ComplianceReportData',
  comment: 'Structured data payload of a compliance report',
  fields: {
    owners: {
      comment: 'Users assigned to the compliance report',
      list: true,
      type: UserPreview,
    },
    teams: {
      comment: 'Teams assigned to the compliance report',
      list: true,
      type: TeamPreview,
    },
    title: {
      comment: 'Title of the compliance report',
      type: 'string',
    },
    description: {
      comment: 'Description of the compliance report',
      type: 'string',
      optional: true,
    },
    columns: {
      comment: 'Ordered list of columns in the compliance report',
      list: true,
      type: ComplianceReportDataColumn,
    },
    rows: {
      comment: 'Ordered list of rows in the compliance report',
      list: true,
      type: ComplianceReportDataRow,
    },
  },
});

/** Override type */
export type ComplianceReportData = SchemaToType<typeof ComplianceReportData>;

export const ComplianceReportDataFilterInput = mkInput({
  name: 'ComplianceReportDataFilterInput',
  comment: 'Input for updating the data of a compliance report',
  fields: {
    id: {
      comment: 'The ID of the compliance report to fetch data for',
      modelName: 'complianceReport',
      type: 'id',
    },
    snapshotId: {
      comment: 'The ID of the compliance report snapshot to fetch data for',
      modelName: 'complianceReportSnapshot',
      optional: true,
      type: 'id',
    },
  },
});

/** Override type */
export type ComplianceReportDataFilterInput = SchemaToType<
  typeof ComplianceReportDataFilterInput
>;

export const ComplianceReportDataRowsFilterInput = mkInput({
  name: 'ComplianceReportDataRowsFilterInput',
  comment: 'Input for filtering rows of a compliance report',
  fields: {
    ...ComplianceReportDataFilterInput.fields,
    text: {
      comment: 'Text to search the cells in the rows for',
      optional: true,
      type: 'string',
    },
  },
});

/** Override type */
export type ComplianceReportDataRowsFilterInput = SchemaToType<
  typeof ComplianceReportDataRowsFilterInput
>;

export const ComplianceReport = mkType({
  name: 'ComplianceReport',
  comment: 'Compliance report of the organization',
  fields: {
    id: {
      comment: 'The unique ID of the compliance report',
      modelName: 'complianceReport',
      type: 'id',
    },
    createdAt: {
      comment: 'The time the compliance report was created',
      type: 'Date',
    },
    updatedAt: {
      comment: 'The time the compliance report was last updated',
      type: 'Date',
    },
    creator: {
      comment: 'The user who created the compliance report',
      type: UserPreview,
      optional: true,
    },
    title: {
      comment: 'The title of the compliance report',
      type: 'string',
    },
    description: {
      comment: 'The description of the compliance report',
      type: 'string',
      optional: true,
    },
    dataProtectionOfficer: {
      comment:
        'The user who is the data protection officer for the compliance report',
      type: UserPreview,
      optional: true,
    },
    teams: {
      comment:
        'The list of teams who are responsible for managing this compliance report',
      list: true,
      type: TeamPreview,
    },
    owners: {
      comment:
        'The list of users who are responsible for managing this compliance report',
      list: true,
      type: UserPreview,
    },
  },
});

/** Override type */
export type ComplianceReport = SchemaToType<typeof ComplianceReport>;

export const ComplianceReportSnapshot = mkType({
  name: 'ComplianceReportSnapshot',
  comment: 'A snapshot of a compliance report of the organization',
  fields: {
    id: {
      comment: 'The unique ID of the compliance report snapshot',
      modelName: 'complianceReportSnapshot',
      type: 'id',
    },
    createdAt: {
      comment: 'The time the compliance report snapshot was created',
      type: 'Date',
    },
    updatedAt: {
      comment: 'The time the compliance report snapshot was last updated',
      type: 'Date',
    },
    creator: {
      comment: 'The user who created the compliance report snapshot',
      type: UserPreview,
      optional: true,
    },
    title: {
      comment: 'The title of the compliance report snapshot',
      type: 'string',
    },
  },
});

/** Override type */
export type ComplianceReportSnapshot = SchemaToType<
  typeof ComplianceReportSnapshot
>;

export const CreateComplianceReportInput = mkInput({
  name: 'CreateComplianceReportInput',
  comment: 'Input for creating a compliance report',
  fields: {
    title: {
      comment: 'The title of the compliance report',
      type: 'string',
    },
    description: {
      comment: 'The description of the compliance report',
      type: 'string',
      optional: true,
    },
    dataProtectionOfficerId: {
      comment:
        'The user ID of the data protection officer for the compliance report',
      type: 'id',
      modelName: 'user',
      optional: true,
    },
    processingActivitiesFilter: {
      comment: 'Filter for which processing activities to include',
      type: ProcessingActivitiesFiltersInput,
      optional: true,
    },
    columns: {
      comment:
        'Columns of the report in order. Should be a ProcessingActivitiesColumnName value or a UUID of an attribute key',
      type: 'string',
      list: true,
    },
  },
});

/** Override type */
export type CreateComplianceReportInput = SchemaToType<
  typeof CreateComplianceReportInput
>;

export const DeleteComplianceReportsInput = mkInput({
  name: 'DeleteComplianceReportsInput',
  comment: 'Input for deleting compliance reports',
  fields: {
    ids: {
      comment: 'The IDs of the compliance reports to delete',
      type: 'id',
      modelName: 'complianceReport',
      list: true,
    },
  },
});

/** Override type */
export type DeleteComplianceReportsInput = SchemaToType<
  typeof DeleteComplianceReportsInput
>;

export const UpdateComplianceReportDataInput = mkInput({
  name: 'UpdateComplianceReportDataInput',
  comment: 'Input for updating a compliance report cell value',
  fields: {
    id: {
      comment: 'The ID of the compliance report to update',
      type: 'id',
      modelName: 'complianceReport',
    },
    rowIndex: {
      comment: 'The index of the row to update',
      type: 'int',
    },
    columnIndex: {
      comment: 'The index of the column to update',
      type: 'int',
    },
    value: {
      comment: 'The new value for the cell',
      type: 'string',
    },
  },
});

/** Override type */
export type UpdateComplianceReportDataInput = SchemaToType<
  typeof UpdateComplianceReportDataInput
>;

export const UpdateComplianceReportInput = mkInput({
  name: 'UpdateComplianceReportInput',
  comment: "Input for updating a compliance report's metadata",
  fields: {
    id: {
      comment: 'The ID of the compliance report to update',
      type: 'id',
      modelName: 'complianceReport',
    },
    fieldName: {
      comment: 'The name of the field to update',
      type: { ComplianceReportMetaField },
    },
    value: {
      comment: 'The new value/values for the field',
      type: 'string',
      list: true,
    },
  },
});

/** Override type */
export type UpdateComplianceReportInput = SchemaToType<
  typeof UpdateComplianceReportInput
>;

export const SaveComplianceReportInput = mkInput({
  name: 'SaveComplianceReportInput',
  comment: 'Input for saving a compliance report snapshot',
  fields: {
    id: {
      comment: 'The ID of the compliance report to save',
      type: 'id',
      modelName: 'complianceReport',
    },
  },
});

/** Override type */
export type SaveComplianceReportInput = SchemaToType<
  typeof SaveComplianceReportInput
>;

/* eslint-enable max-lines */
